import type { MorphemeInfoPanelAccordionSectionDescriptor } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content._components.morpheme-info-panel-accordion";
import { useMorphemeInfoPanelCaligraphyGraphemeSections } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content.views.caligraphy";
import { useMorphemeInfoPanelDictionaryGraphemeSections } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content.views.dictionary";
import { useMorphemeInfoPanelLearningProgressGraphemeSections } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content.views.learning-progress";
import type { ReferenceGraphemeMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";

export interface MorphemeInfoPanelSectionGroup {
  key: string;
  sections: MorphemeInfoPanelAccordionSectionDescriptor[];
  title: string;
}

/**
 * Every info-panel section for a grapheme, grouped by the tab it normally lives in.
 *
 * The Reference page shows all of these on one page, so it needs the DESCRIPTORS rather than the
 * tab components: mounting the tabs would stack several scroll containers, each `flex-1` and so
 * zero-height inside an outer scroll, which silently blanks every section. Each `views/*` package
 * exposes its own descriptor hook (its `__sections/*` are private to it), and this composes them.
 *
 * Grapheme-only by design: a multi-codepoint word never reaches Reference — the panels layout
 * branches it to `MorphemePanelsWordView` first — so the word-specific section hooks are not called.
 */
export function useMorphemeInfoPanelGraphemeGroupedSections({
  graphemeCard,
}: {
  graphemeCard: ReferenceGraphemeMorphemeSlice;
}): MorphemeInfoPanelSectionGroup[] {
  const dictionary = useMorphemeInfoPanelDictionaryGraphemeSections({ graphemeCard });
  const caligraphy = useMorphemeInfoPanelCaligraphyGraphemeSections({ graphemeCard });
  const learningProgress = useMorphemeInfoPanelLearningProgressGraphemeSections({ graphemeCard });

  return [
    { key: "dictionary", sections: dictionary, title: "Dictionary" },
    { key: "caligraphy", sections: caligraphy, title: "Calligraphy" },
    { key: "progress", sections: learningProgress, title: "Progress" },
  ].filter((group) => group.sections.length > 0);
}
